import React, { useCallback, useEffect } from "react";

import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";

import { formatAnnualIncrease } from "../../Shared/helpers/formatAnnualIncrease";
import { generateStartAndEndPlanDatesFromForm } from "../../Shared/helpers/generateStartAndEndPlanDatesFromForm";
import { getFromAccountFromOptions } from "../../Shared/helpers/getFromAccountFromOptions";
import { IncomeFormProps, PickIncomeFormFields } from "../Types";
import {
  AmountField,
  EndsField,
  NameField,
  OwnerField,
  RepeatsField,
  StartsField,
  ToAccountField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import FormDrawer from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FormDrawer/FormDrawer";
import AnnualIncreaseMultiDropdownInput, {
  AnnualIncreaseAmountField,
  AnnualIncreaseFields,
  AnnualIncreaseTypeField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/MultiDropdown/AnnualIncrease/AnnualIncrease";
import RepeatsMultiDropdownInput from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/MultiDropdown/Repeats/Repeats";
import type { RepeatDropdownFormFieldTypes } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Types";
import { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import {
  formNumberFieldWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
} from "@src/utils/formValidation";

type PickedFormFields = PickIncomeFormFields<
  "OwnerField" | "NameField" | "AmountField" | "ToAccountField"
>;

type Form = PickedFormFields &
  AnnualIncreaseFields &
  RepeatDropdownFormFieldTypes;

const SalaryIncomeForm: React.FC<IncomeFormProps> = ({
  drawerOpen,
  onClose,
  mutations,
  goalBasedFinancialPlanIncomeId,
  existingIncomeElement,
  retirementHorizons,
  initialPlanDateOwnerAndAnnualIncreaseFields,
  hideSpouseOptions,
  ownersOptions,
  addedAccountOptions,
}) => {
  const { goalBasedFinancialPlanId } = useParams<{
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId;
  }>();

  const notify = useStatusNotification();

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [NameField]: undefined,
      [AmountField]: undefined,
      [ToAccountField]: getFromAccountFromOptions(
        existingIncomeElement?.depositAccount,
        addedAccountOptions
      ),
      ...initialPlanDateOwnerAndAnnualIncreaseFields,
    },
  });

  const {
    formState: { isDirty, isValid },
    getValues,
    reset,
    watch,
  } = methods;
  const annualIncreaseTypeValue = watch(AnnualIncreaseTypeField);
  const repeatsValue = watch(RepeatsField);
  const startsValue = watch(StartsField);
  const endsValue = watch(EndsField);

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      reset({
        [NameField]: existingIncomeElement?.name,
        [AmountField]: existingIncomeElement?.amount,
        [ToAccountField]: getFromAccountFromOptions(
          existingIncomeElement?.depositAccount,
          addedAccountOptions
        ),
        ...initialPlanDateOwnerAndAnnualIncreaseFields,
      });
    }
  }, [
    drawerOpen,
    reset,
    initialPlanDateOwnerAndAnnualIncreaseFields,
    existingIncomeElement,
    addedAccountOptions,
  ]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  const onSave = async () => {
    const values = getValues();

    if (
      values[NameField] === undefined ||
      values[AmountField] === undefined ||
      values[ToAccountField] === undefined
    ) {
      return;
    }

    if (retirementHorizons === undefined) {
      throw new Error("retirementHorizons is undefined");
    }

    const annualIncrease = formatAnnualIncrease(
      values[AnnualIncreaseTypeField].value,
      values[AnnualIncreaseAmountField]
    );

    const [startDate, endDate] = generateStartAndEndPlanDatesFromForm({
      clientId: values[OwnerField].value,
      endPlanDateId:
        existingIncomeElement !== undefined
          ? existingIncomeElement.endDate?.planDateId
          : undefined,
      retirementHorizons,
      startPlanDateId:
        existingIncomeElement !== undefined
          ? existingIncomeElement.startDate?.planDateId
          : undefined,
      form: values,
    });

    if (goalBasedFinancialPlanIncomeId !== undefined) {
      if (existingIncomeElement === undefined) {
        return;
      }

      try {
        await mutations.onUpdateIncomeSource({
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanIncomeId,
          body: {
            incomeType: "Salary",
            owner: values[OwnerField].value,
            name: values[NameField],
            amount: removeCommas(values[AmountField]),
            depositAccount: values[ToAccountField].value,
            annualIncrease,
            startDate,
            endDate,
            repeats: values[RepeatsField].value,
          },
        });

        notify("Income added", "Success");

        handleOnClose();
      } catch (error) {
        if (isAxiosError<ErrorMessage>(error)) {
          const message = error.response?.data.message;
          notify(`Error adding income: ${message ?? ""}`, "Error");
        } else {
          notify("Error adding income", "Error");
        }
      }
    } else {
      try {
        await mutations.onAddIncomeSource({
          goalBasedFinancialPlanId,
          body: {
            incomeType: "Salary",
            owner: values[OwnerField].value,
            name: values[NameField],
            amount: removeCommas(values[AmountField]),
            depositAccount: values[ToAccountField].value,
            annualIncrease,
            startDate,
            endDate,
            repeats: values[RepeatsField].value,
          },
        });

        notify("Income added", "Success");

        handleOnClose();
      } catch (error) {
        if (isAxiosError<ErrorMessage>(error)) {
          const message = error.response?.data.message;
          notify(`Error adding income: ${message ?? ""}`, "Error");
        } else {
          notify("Error adding income", "Error");
        }
      }
    }
  };

  return (
    <FormDrawer
      title={"Salary"}
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={mutations.isMutating || !isValid || !isDirty}
      onSave={onSave}
      removeDisabled={mutations.isMutating}
      onRemove={
        existingIncomeElement
          ? () => mutations.onDeleteIncomeSource("Salary")
          : undefined
      }
      isLoading={mutations.isMutating}
      hasError={false}
      deleteModalTitle={"Remove Income"}
      deleteModalSubTitle={"Are you sure you want to remove this income?"}
    >
      <FormProvider {...methods}>
        <FlexWrapper>
          <FormDropdownField
            name={OwnerField}
            label={"Owner"}
            values={ownersOptions}
            disabled={existingIncomeElement !== undefined}
            disableSearch={true}
            required={"Must not be empty"}
          />

          <FormTextField
            name={NameField}
            label="Name"
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormDropdownField
            name={ToAccountField}
            label="To Account"
            values={addedAccountOptions}
            disableSearch={true}
            required={"Must not be empty"}
          />

          <FormNumberField
            name={AmountField}
            label="Amount"
            startAdornment={"$"}
            customRegex={formNumberFieldWithCommasCustomRegex}
            rules={formNumberFieldWithCommasRules({
              required: true,
              minValue: 0,
              maxValue: 100_000_000,
            })}
            autoAddCommas={true}
          />
        </FlexWrapper>

        <FlexWrapper>
          <AnnualIncreaseMultiDropdownInput
            fieldLabel={"Annual Increase Type"}
            additionalInputFieldLabel={"Custom Annual Inflation"}
            curOption={annualIncreaseTypeValue.value}
          />
        </FlexWrapper>

        <FlexWrapper>
          <RepeatsMultiDropdownInput
            curRepeatsOption={repeatsValue.value}
            curPlanDateStartOption={startsValue.value}
            curPlanDateEndOption={endsValue.value}
            hideSpouseOptions={hideSpouseOptions}
          />
        </FlexWrapper>
      </FormProvider>
    </FormDrawer>
  );
};

export default SalaryIncomeForm;
